import { ArrowRight, Check, Users, Target, Calendar, Award, AlertTriangle, Lightbulb } from 'lucide-react';

const SeminairePage = () => {
  const painPoints = [
    {
      category: 'Vision',
      description: 'Vision floue ou trop théorique, objectifs long terme non traduits en trajectoire concrète, priorités changeantes.',
      risk: 'Désalignement stratégique et décisions contradictoires.'
    },
    {
      category: 'Organisation',
      description: 'Silos inter-services, rôles et responsabilités flous, conflits entre directions, gouvernance peu lisible.',
      risk: "Blocages organisationnels et perte d'efficacité collective."
    },
    {
      category: 'Roadmap & Exécution',
      description: "Trop de projets lancés, peu finalisés. Roadmap non priorisée, manque de suivi post-séminaire.",
      risk: 'Transformation permanente sans impact mesurable.'
    },
    {
      category: 'Enjeux RH & Cohésion',
      description: "Manque de cohésion, tensions inter-équipes, méfiance entre services, turnover, perte de sens.",
      risk: "Baisse d'engagement et dégradation du climat interne."
    }
  ];

  const valuePromises = [
    { icon: Target, title: 'Clarifier la trajectoire', desc: 'Une vision partagée, concrète et opérationnelle.' },
    { icon: Users, title: 'Aligner la Direction', desc: "Un CODIR soudé qui parle d'une seule voix." },
    { icon: Lightbulb, title: 'Prioriser vos enjeux', desc: 'Des priorités claires, arbitrées et assumées.' },
    { icon: Award, title: 'Responsabiliser les acteurs', desc: 'Des sponsors désignés pour chaque chantier.' },
    { icon: Calendar, title: 'Installer la gouvernance', desc: "Un cadre d'exécution et de suivi durable." }
  ];

  const seminarSteps = [
    { number: '1', title: 'Ouverture stratégique', items: ['Vision', 'Ambition', 'Enjeux'] },
    { number: '2', title: 'Restitution du diagnostic', items: ["Forces de l'organisation", 'Points de tension', 'Enjeux prioritaires'] },
    { number: '3', title: 'Debrief en atelier collaboratif', items: ['Partage des constats', 'Clarification des enjeux'] },
    { number: '4', title: 'Présentation des travaux en plénière', items: ['Présentation', 'Engagement', 'Responsabilisation'] },
    { number: '5', title: 'Priorisation', items: ['Chantiers stratégiques', 'Responsables', 'Premières actions'] },
    { number: '6', title: 'Engagement collectif', items: ['Validation de la feuille de route'] }
  ];

  const references = [
    {
      company: 'Entreprise aéronautique',
      problem: "Manque de cohésion et d'alignement des équipes dirigeantes.",
      actions: '2 séminaires d\'alignement (20 collaborateurs), redéfinition de la Vision 2025-2027.',
      result: 'Alignement sur une roadmap et des objectifs ambitieux pour les 3 prochaines années.'
    },
    {
      company: 'Entreprise d\'expertises en assurance',
      problem: 'Difficulté à aligner 40 personnes sur une organisation cible.',
      actions: 'Clarification du modèle opérationnel cible, 100 actions identifiées et priorisées.',
      result: 'Alignement sur un modèle opérationnel et sur les chantiers prioritaires.'
    },
    {
      company: 'Scale up dans le digital',
      problem: 'Forte croissance, difficultés de collaboration inter-équipes.',
      actions: 'Séminaire de transformation agile, clarification gouvernance Produit / Métier / Tech.',
      result: 'Alignement des équipes Product, Tech & Business sur un modèle opérationnel commun.'
    }
  ];

  const pricing = [
    {
      name: 'ESSENTIEL',
      subtitle: "Séminaire d'Alignement CODIR",
      price: '5 000',
      target: 'CODIR souhaitant clarifier sa vision, direction préparant une nouvelle feuille de route.',
      features: [
        'Aide à la préparation du séminaire',
        'Animation du séminaire',
        'Rapport de synthèse'
      ],
      featured: false
    },
    {
      name: 'ALIGNEMENT STRATÉGIQUE',
      subtitle: 'Diagnostic + Offsite élargi',
      price: '12 000 – 18 000',
      target: 'Organisation en tension, manque de cohésion inter-services, transformation déjà engagée mais peu structurée.',
      features: [
        '10 Interviews de collaborateurs',
        'Co-construction des supports de séminaires',
        'Plénières et groupes de travail',
        'Rapport de Synthèse'
      ],
      featured: true
    },
    {
      name: 'TRANSFORMATION',
      subtitle: 'Diagnostic + Offsite + Suivi 3 mois',
      price: '25 000 – 35 000',
      target: 'Entreprise en forte croissance, réorganisation majeure, nouvelle stratégie 3 ans.',
      features: [
        '10 Interviews de collaborateurs',
        'Co-construction des supports de séminaires',
        'Plénières et groupes de travail',
        'Rapport de Synthèse',
        'Suivi projets 3 mois'
      ],
      featured: false
    }
  ];

  const results = [
    { time: 'Sortie de séminaire', items: 'Vision partagée, Roadmap validée, sponsors désignés, plan d\'action 90 jours activé, cohésion d\'équipe' },
    { time: 'À 3 mois', items: 'Réduction des tensions inter-équipes, exécution accélérée des priorités, responsabilisation des managers.' },
    { time: 'À 6 mois', items: 'Transformation tangible et mesurable, meilleure coordination transverse, climat interne amélioré.' }
  ];

  return (
    <div data-testid="seminaire-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#0a1628] text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1761461535556-9bec8112eaab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwZ2xhc3MlMjBidWlsZGluZyUyMGRldGFpbCUyMGFic3RyYWN0JTIwYmx1ZSUyMHdoaXRlfGVufDB8fHx8MTc3MzY2NDc1MHww&ixlib=rb-4.1.0&q=85')" 
          }}
        />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <p className="caption-text text-[#BFA15F] mb-4">Séminaire de Transformation</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] font-heading text-white mb-6">
              Executive Transformation<br />
              <span className="text-[#BFA15F]">Offsite™</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Un dispositif structurant pour réaligner votre vision, votre organisation 
              et vos équipes — et transformer l'ambition en exécution.
            </p>
            <div className="flex flex-wrap gap-4">
              <span className="px-4 py-2 border border-slate-500 text-sm text-slate-300">Séminaire dirigeants</span>
              <span className="px-4 py-2 border border-slate-500 text-sm text-slate-300">Équipe de Direction</span>
              <span className="px-4 py-2 border border-[#BFA15F] text-sm text-[#BFA15F]">Transformation & Alignement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Constat</p>
            <h2 className="heading-lg mb-6">
              Quand la croissance s'accélère,<br />
              <span className="text-slate-400">l'alignement devient un avantage concurrentiel.</span>
            </h2>
            <p className="body-large">
              Les entreprises qui traversent une transformation réussie ne sont pas celles 
              qui ont la meilleure stratégie sur le papier — ce sont celles dont les équipes 
              dirigeantes avancent ensemble, avec clarté et engagement.
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section data-testid="pain-points-section" className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Diagnostic</p>
            <h2 className="heading-lg">Les points de douleur que nous adressons</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {painPoints.map((point, index) => (
              <div key={index} className="bg-white p-8 border border-slate-100 hover:border-[#0a1628] transition-all">
                <h3 className="font-heading font-semibold text-xl mb-4 text-[#0a1628]">{point.category}</h3>
                <p className="body-text mb-4">{point.description}</p>
                <div className="flex items-start text-sm text-amber-700 bg-amber-50 p-3 border border-amber-100">
                  <AlertTriangle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Risque :</strong> {point.risk}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Promise */}
      <section data-testid="value-promise-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="heading-lg mb-4">Qu'attendre d'un séminaire de transformation ?</h2>
            <p className="body-text max-w-2xl mx-auto">
              Ce n'est pas un simple "Team Building". C'est un dispositif <strong>structurant</strong>, <strong>engageant</strong> et <strong>mobilisant</strong> les équipes.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {valuePromises.map((promise, index) => (
              <div key={index} className="text-center p-6 bg-slate-50 border border-slate-100">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white text-[#0a1628]">
                  <promise.icon size={24} strokeWidth={1.5} />
                </div>
                <h4 className="font-heading font-semibold text-sm mb-2">{promise.title}</h4>
                <p className="text-xs text-slate-500">{promise.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seminar Flow */}
      <section data-testid="seminar-flow-section" className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Déroulé</p>
            <h2 className="heading-lg">Comment se déroule une journée type ?</h2>
            <p className="body-text mt-4">Une journée structurée pour produire des résultats concrets</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {seminarSteps.map((step, index) => (
              <div key={index} className="bg-white p-4 border border-slate-100 hover:border-[#0a1628] transition-all flex-1 min-w-[150px] max-w-[180px]">
                <div className="w-8 h-8 flex items-center justify-center bg-[#0a1628] text-white font-bold text-sm mb-3">
                  {step.number}
                </div>
                <h4 className="font-heading font-semibold text-sm mb-2">{step.title}</h4>
                <ul className="space-y-1">
                  {step.items.map((item, idx) => (
                    <li key={idx} className="text-xs text-slate-600 flex items-center">
                      <span className="w-1 h-1 rounded-full bg-[#BFA15F] mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section data-testid="results-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Résultats</p>
            <h2 className="heading-lg">Après le séminaire, sécurisez votre exécution</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {results.map((result, index) => (
              <div key={index} className="relative p-8 border-l-4 border-[#BFA15F] bg-slate-50">
                <span className="absolute -top-3 left-4 bg-white px-2 text-[#BFA15F] font-semibold text-sm">{result.time}</span>
                <p className="body-text mt-4">{result.items}</p>
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-3 gap-8 mt-16 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-[#0a1628] font-heading">5-8</div>
              <p className="text-sm text-slate-500 uppercase tracking-wider mt-2">Chantiers prioritaires validés</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-[#0a1628] font-heading">90<span className="text-[#BFA15F]">j</span></div>
              <p className="text-sm text-slate-500 uppercase tracking-wider mt-2">Plan d'action opérationnel</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-[#0a1628] font-heading">6<span className="text-[#BFA15F]">M</span></div>
              <p className="text-sm text-slate-500 uppercase tracking-wider mt-2">Suivi actif d'accompagnement</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client References */}
      <section data-testid="client-references-section" className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="heading-lg">Références clients</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {references.map((ref, index) => (
              <div key={index} className="bg-white p-8 border border-slate-100 hover:shadow-lg transition-all">
                <h4 className="font-heading font-bold text-xl text-[#0a1628] mb-4">{ref.company}</h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Problématique</p>
                    <p className="text-sm text-slate-600">{ref.problem}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Actions</p>
                    <p className="text-sm text-slate-600">{ref.actions}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#BFA15F] mb-1">Résultat</p>
                    <p className="text-sm text-slate-700 font-medium">{ref.result}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Offres</p>
            <h2 className="heading-lg">Offres</h2>
            <p className="body-text mt-4 max-w-2xl mx-auto">
              Trois niveaux d'engagement pour répondre à la maturité et à l'ambition de votre organisation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {pricing.map((plan, index) => (
              <div 
                key={index} 
                data-testid={`pricing-card-${index}`}
                className={`pricing-card ${plan.featured ? 'featured' : ''}`}
              >
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-wider text-[#BFA15F] font-semibold mb-2">Offre {index + 1}</p>
                  <h3 className="font-heading font-bold text-xl text-[#0a1628] mb-1">{plan.name}</h3>
                  <p className="text-sm text-slate-500">{plan.subtitle}</p>
                </div>
                
                <p className="text-sm text-slate-600 mb-6 pb-6 border-b border-slate-100">
                  <strong>Pour qui ?</strong> {plan.target}
                </p>
                
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">Contenu de l'offre</p>
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-600">
                        <Check size={16} className="mr-2 mt-0.5 text-[#BFA15F] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <a 
                  href="mailto:yoann.cohen@wyco.fr?subject=Demande%20d'information%20-%20Offre%20Séminaire"
                  className={`mt-8 w-full ${plan.featured ? 'btn-gold' : 'btn-secondary'}`}
                >
                  Demander un devis
                </a>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-8">
            Toutes nos offres sont personnalisées selon la taille de votre organisation, 
            le niveau de complexité et vos objectifs spécifiques. 
            <strong className="text-[#0a1628]"> Un premier échange de cadrage est offert.</strong>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0a1628] text-white">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-6">
            Prêt pour amorcer votre transformation ?
          </h2>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-10">
            Discutons de vos enjeux lors d'un premier échange de cadrage offert.
          </p>
          <a 
            href="mailto:yoann.cohen@wyco.fr?subject=Séminaire%20Executive%20Transformation%20Offsite"
            data-testid="seminaire-contact-btn"
            className="btn-gold"
          >
            Planifier un échange
            <ArrowRight size={16} className="ml-2" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default SeminairePage;
